// /documentos — documentos del proyecto (ADR-0144 §4, fase 1).
//
// La inmobiliaria elige un proyecto, ve sus documentos vigentes, sube un
// PDF (título y tipo) y retira lo que ya no corresponde. Retirar no
// borra: el documento deja de verse en la app del propietario y la fila
// queda en la base. El acta de entrega no se sube acá (es de una unidad,
// no del proyecto) y tampoco se muestra todavía al propietario: va en un
// corte propio (ADR-0144 §4, nota de implementación).
//
// Subir = presign (core-api → media-svc, prefijo del proyecto) → PUT
// directo al bucket → registrar. El tipo y el tamaño se validan acá para
// avisar rápido y el servidor los vuelve a validar contra el objeto.
//
// La pantalla existe sólo con la receta y el rol del importador de
// unidades (canImportUnits); App.tsx redirige si no.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DOCUMENT_CONTENT_TYPE,
  DOCUMENT_KIND_LABEL,
  DOCUMENT_MAX_BYTES,
  DOCUMENT_TITLE_MAX,
  PROJECT_DOCUMENT_KINDS,
  type ProjectDocument,
  type ProjectDocumentKind,
} from "@bitacorapro/api-types";
import { Icon } from "../components/icons";
import { PageSkeleton } from "../components/PageSkeleton";
import { Skel } from "../components/Skel";
import { SkelTable } from "../components/skeleton/SkelTable";
import { useCarteraProyectos } from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import { documentsApi, putToStorage } from "../lib/api/endpoints/documents";
import { ApiError } from "../lib/api/error";
import { RefreshNotices, SlowNotice } from "./queryViewKit";

const documentsKey = (groupId: string) => ["project-documents", groupId] as const;

const MB = 1024 * 1024;

/** El mensaje de validación del archivo en el cliente, o null si sirve. */
export function fileProblem(file: File): string | null {
  const isPdf = file.type === DOCUMENT_CONTENT_TYPE || file.name.toLowerCase().endsWith(".pdf");
  if (!isPdf) return "Solo se aceptan archivos PDF.";
  if (file.size === 0) return "El archivo está vacío.";
  if (file.size > DOCUMENT_MAX_BYTES) {
    return `El archivo pesa ${(file.size / MB).toFixed(1)} MB y el máximo es ${DOCUMENT_MAX_BYTES / MB} MB.`;
  }
  return null;
}

function formatSize(bytes: number): string {
  return bytes >= MB
    ? `${(bytes / MB).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" });
}

function errorMessage(err: unknown, fallback: string): string {
  // Un 5xx llega enmascarado («Something went wrong») y uno de red trae
  // el texto del navegador: en ambos casos, el mensaje propio.
  if (err instanceof ApiError)
    return err.status === 0 || err.status >= 500 ? fallback : err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function DocumentosProyecto() {
  const proyectosQuery = useCarteraProyectos({ limit: 200 });
  const proyectos = proyectosQuery.data;
  // ADR-0166: el selector pasa por el contrato de carga (skeleton con
  // umbral; vacío y error distintos de «cargando»).
  const proyectosView = useQueryView(proyectosQuery, {
    isEmpty: (d) => d.items.length === 0,
  });
  const [groupId, setGroupId] = useState<string>("");
  // Mientras sube un PDF no se cambia de proyecto: la subida está atada
  // al proyecto elegido y cambiarlo desmontaría su progreso.
  const [uploading, setUploading] = useState(false);

  // Con un solo proyecto (o al llegar la lista) se preselecciona el primero.
  useEffect(() => {
    if (!groupId && proyectos?.items[0]) setGroupId(proyectos.items[0].id);
  }, [groupId, proyectos]);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">Gestión</div>
          <h1 className="page-header__title">Documentos</h1>
          <p className="page-header__sub">
            Sube el manual, el reglamento y los planos de cada proyecto. Los propietarios ven los
            documentos vigentes en su app. Solo PDF, hasta {DOCUMENT_MAX_BYTES / MB} MB.
          </p>
        </div>
      </div>

      <div className="stack-gap">
        <section className="panel">
          <div className="panel__body">
            <div className="field">
              <label className="field__label" htmlFor="documents-project">
                Proyecto
              </label>
              {proyectosView.kind === "loading" ? (
                <>
                  <PageSkeleton
                    label="Cargando proyectos…"
                    visible={proyectosView.visible}
                    kind="section"
                  >
                    <Skel width="100%" height={38} />
                  </PageSkeleton>
                  {proyectosView.slow && (
                    <SlowNotice onRetry={() => void proyectosQuery.refetch()} />
                  )}
                </>
              ) : proyectosView.kind === "error" ? (
                <div className="documents__actions">
                  <p className="auth-error" role="alert">
                    No pudimos cargar los proyectos.
                  </p>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={proyectosView.retry}
                  >
                    Reintentar
                  </button>
                </div>
              ) : proyectosView.kind !== "ready" ? (
                <p className="cell-meta">Aún no hay proyectos en tu organización.</p>
              ) : (
                <select
                  id="documents-project"
                  className="select"
                  value={groupId}
                  disabled={uploading}
                  aria-describedby={uploading ? "documents-project-busy" : undefined}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  {proyectos?.items.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
              {uploading && (
                <p className="field__hint" id="documents-project-busy">
                  Espera a que termine la subida para cambiar de proyecto.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Tras el hold del skeleton del selector: así nunca hay dos
            anuncios de carga a la vez en la página. */}
        {groupId && proyectosView.kind === "ready" && (
          <ProjectDocuments key={groupId} groupId={groupId} onUploadingChange={setUploading} />
        )}
      </div>
    </div>
  );
}

function ProjectDocuments({
  groupId,
  onUploadingChange,
}: {
  groupId: string;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: documentsKey(groupId),
    queryFn: () => documentsApi.list(groupId),
  });
  const [openError, setOpenError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [confirmRetire, setConfirmRetire] = useState<string | null>(null);

  const retire = useMutation({
    mutationFn: (documentId: string) => documentsApi.retire(groupId, documentId),
    onSuccess: () => {
      setConfirmRetire(null);
      void qc.invalidateQueries({ queryKey: documentsKey(groupId) });
    },
    onError: (err) => {
      // Otro lo retiró antes: la lista está desactualizada.
      if (err instanceof ApiError && err.status === 404) {
        setConfirmRetire(null);
        void qc.invalidateQueries({ queryKey: documentsKey(groupId) });
      }
    },
  });

  async function open(doc: ProjectDocument) {
    if (opening) return;
    // La pestaña se abre dentro del clic; la URL (vida corta) llega después.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    setOpening(doc.id);
    setOpenError(null);
    try {
      const { url } = await documentsApi.url(groupId, doc.id);
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      tab?.close();
      setOpenError(errorMessage(err, "No pudimos abrir el documento."));
    } finally {
      setOpening(null);
    }
  }

  // ADR-0166: carga / error / vacío / listo por el contrato común. Una
  // revisión que falla sobre la lista ya cargada la conserva y avisa.
  const view = useQueryView(list, { isEmpty: (d) => d.documents.length === 0 });
  const docs = view.kind === "ready" ? view.data.documents : [];

  return (
    <>
      <UploadPanel groupId={groupId} onUploadingChange={onUploadingChange} />

      <section className="panel" aria-labelledby="documents-current">
        <div className="panel__head">
          <h2 className="panel__title" id="documents-current">
            Vigentes
          </h2>
          <p className="panel__sub">Lo que ven hoy los propietarios de este proyecto.</p>
        </div>
        <div className="panel__body stack-gap">
          {view.kind === "loading" && (
            <>
              <PageSkeleton label="Cargando documentos…" visible={view.visible}>
                <SkelTable
                  rows={3}
                  cols={5}
                  headers={["Título", "Tipo", "Publicado", "Tamaño", ""]}
                />
              </PageSkeleton>
              {view.slow && <SlowNotice onRetry={() => void list.refetch()} />}
            </>
          )}
          {view.kind === "error" && (
            <div className="documents__actions">
              <p className="auth-error" role="alert">
                {errorMessage(view.error, "No pudimos cargar los documentos.")}
              </p>
              <button type="button" className="btn btn--secondary btn--sm" onClick={view.retry}>
                Reintentar
              </button>
            </div>
          )}
          {view.kind === "ready" && (
            <RefreshNotices
              refreshFailed={view.refreshFailed}
              revalidatingHint={view.revalidatingHint}
              onRetry={() => void list.refetch()}
            />
          )}
          {view.kind === "empty" && (
            <p className="cell-meta">
              Este proyecto no tiene documentos. Mientras no subas ninguno, el propietario no ve la
              sección Documentos en su app.
            </p>
          )}
          {openError && (
            <p className="auth-error" role="alert">
              {openError}
            </p>
          )}
          {retire.isError && (
            <p className="auth-error" role="alert">
              {retire.error instanceof ApiError && retire.error.status === 404
                ? "Ese documento ya no estaba vigente. Actualizamos la lista."
                : errorMessage(retire.error, "No pudimos retirar el documento.")}
            </p>
          )}
          {docs.length > 0 && (
            <table className="dtable">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Tipo</th>
                  <th>Publicado</th>
                  <th className="col-num">Tamaño</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td className="cell-strong">{d.title}</td>
                    <td>{DOCUMENT_KIND_LABEL[d.kind]}</td>
                    <td>{formatDate(d.publishedAt)}</td>
                    <td className="col-num">{formatSize(d.sizeBytes)}</td>
                    <td>
                      {confirmRetire === d.id ? (
                        <div
                          className="documents__actions"
                          role="group"
                          aria-label="Confirmar retiro"
                        >
                          <span className="cell-meta">¿Retirar? Deja de verse en la app.</span>
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            disabled={retire.isPending}
                            onClick={() => retire.mutate(d.id)}
                          >
                            {retire.isPending ? "Retirando…" : "Sí, retirar"}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={retire.isPending}
                            onClick={() => setConfirmRetire(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="documents__actions">
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            disabled={opening !== null}
                            onClick={() => void open(d)}
                          >
                            <Icon.External s={12} />
                            {opening === d.id ? "Abriendo…" : "Ver"}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                              retire.reset();
                              setConfirmRetire(d.id);
                            }}
                          >
                            Retirar
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}

function UploadPanel({
  groupId,
  onUploadingChange,
}: {
  groupId: string;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  // El título que se autocompletó desde el nombre del archivo. Mientras
  // el campo siga igual a esto, elegir otro archivo lo reemplaza; si la
  // persona lo editó, se respeta.
  const [autoTitle, setAutoTitle] = useState<string | null>(null);
  const [kind, setKind] = useState<ProjectDocumentKind>("manual");
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // p11 — el intento de subida en curso: la Idempotency-Key del registro
  // y, si ya subió, el objeto. Un reintento del MISMO archivo, título y
  // tipo reusa ambos: si el registro anterior sí entró (se perdió la
  // respuesta), el servidor devuelve ese mismo documento en vez de
  // publicar otro, y no se sube el PDF de nuevo. Cambiar algo es otro
  // intento. Sólo un éxito lo libera.
  const attemptRef = useRef<{
    file: File;
    title: string;
    kind: ProjectDocumentKind;
    key: string;
    objectKey: string | null;
  } | null>(null);

  const upload = useMutation({
    mutationFn: async (args: { file: File; title: string; kind: ProjectDocumentKind }) => {
      const prev = attemptRef.current;
      const attempt =
        prev && prev.file === args.file && prev.title === args.title && prev.kind === args.kind
          ? prev
          : { ...args, key: crypto.randomUUID(), objectKey: null };
      attemptRef.current = attempt;
      if (attempt.objectKey === null) {
        const presign = await documentsApi.uploadUrl(groupId, {
          contentType: DOCUMENT_CONTENT_TYPE,
          contentLength: args.file.size,
        });
        // El presign firmó application/pdf: el cuerpo viaja con ese tipo
        // aunque el navegador no lo haya detectado (archivo sin tipo).
        const body =
          args.file.type === DOCUMENT_CONTENT_TYPE
            ? args.file
            : new Blob([args.file], { type: DOCUMENT_CONTENT_TYPE });
        await putToStorage(presign, body);
        attempt.objectKey = presign.objectKey;
      }
      return documentsApi.create(
        groupId,
        { objectKey: attempt.objectKey, title: args.title, kind: args.kind },
        attempt.key
      );
    },
    onSuccess: (doc) => {
      attemptRef.current = null;
      setDone(`«${doc.title}» quedó publicado.`);
      setFile(null);
      setTitle("");
      setAutoTitle(null);
      if (fileRef.current) fileRef.current.value = "";
      void qc.invalidateQueries({ queryKey: documentsKey(groupId) });
    },
  });

  const uploading = upload.isPending;
  useEffect(() => {
    onUploadingChange(uploading);
  }, [uploading, onUploadingChange]);
  // Al desmontar (cambio de proyecto) nunca queda el selector bloqueado.
  useEffect(() => () => onUploadingChange(false), [onUploadingChange]);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    setDone(null);
    upload.reset();
    setFile(picked);
    setProblem(picked ? fileProblem(picked) : null);
    // El nombre del archivo como punto de partida del título, salvo que
    // la persona ya haya escrito uno propio.
    if (picked && (!title.trim() || title === autoTitle)) {
      const next = picked.name.replace(/\.pdf$/i, "").slice(0, DOCUMENT_TITLE_MAX);
      setTitle(next);
      setAutoTitle(next);
    }
  }

  const trimmed = title.trim();
  const canUpload = file !== null && problem === null && trimmed.length > 0 && !upload.isPending;

  return (
    <section className="panel" aria-labelledby="documents-upload">
      <div className="panel__head">
        <h2 className="panel__title" id="documents-upload">
          Subir documento
        </h2>
        <p className="panel__sub">
          Se publica de inmediato para todos los propietarios del proyecto.
        </p>
      </div>
      <form
        className="panel__body stack-gap"
        onSubmit={(e) => {
          e.preventDefault();
          if (canUpload && file) upload.mutate({ file, title: trimmed, kind });
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="documents-file">
            Archivo PDF
          </label>
          <input
            id="documents-file"
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            disabled={upload.isPending}
            onChange={onPick}
            aria-describedby={problem ? "documents-file-problem" : undefined}
          />
          {file && !problem && (
            <p className="field__hint">
              {file.name} · {formatSize(file.size)}
            </p>
          )}
          {problem && (
            <p className="auth-error" id="documents-file-problem" role="alert">
              {problem}
            </p>
          )}
        </div>
        <div className="documents__fields">
          <div className="field">
            <label className="field__label" htmlFor="documents-title">
              Título
            </label>
            <input
              id="documents-title"
              className="input"
              type="text"
              maxLength={DOCUMENT_TITLE_MAX}
              value={title}
              disabled={upload.isPending}
              placeholder="Ej.: Manual de uso y mantención"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="documents-kind">
              Tipo
            </label>
            <select
              id="documents-kind"
              className="select"
              value={kind}
              disabled={upload.isPending}
              onChange={(e) => setKind(e.target.value as ProjectDocumentKind)}
            >
              {PROJECT_DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {DOCUMENT_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="documents__actions">
          <button type="submit" className="btn btn--primary" disabled={!canUpload}>
            {upload.isPending ? "Subiendo…" : "Subir y publicar"}
          </button>
        </div>
        {/* Progreso de la subida (una acción, no la carga de la pantalla):
            fuera de la norma de skeleton (ADR-0166). */}
        {upload.isPending && (
          <p className="cell-meta" role="status">
            Subiendo el PDF…
          </p>
        )}
        {upload.isError && (
          <p className="auth-error" role="alert">
            {errorMessage(upload.error, "No pudimos subir el documento. Intenta de nuevo.")}
          </p>
        )}
        {done && (
          <p className="notice-banner" role="status">
            {done}
          </p>
        )}
      </form>
    </section>
  );
}
